import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FeedbackImageType, FeedbackType, fileToBase64, sendFeedback } from "../service/FeedbackService";

// "Giv feedback" in the admin panel: an idea, a bug or praise to RH Design. Fields, texts and rules follow the
// feedback engine's integration guide (rhdesign-feedback/docs/integration.md, 3b) in the admin's MUI look.
const MAX_TEXT = 4000;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES: FeedbackImageType[] = ["image/png", "image/jpeg", "image/webp"];
const TYPES: { value: FeedbackType; color: string }[] = [
  { value: "ide", color: "#ED670A" },
  { value: "fejl", color: "#C62828" },
  { value: "ros", color: "#2E7D32" },
];

export default function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [t] = useTranslation("global");
  const { pathname } = useLocation();
  const [type, setType] = useState<FeedbackType | "">("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [contact, setContact] = useState(false);
  const [errors, setErrors] = useState<{ type?: string; text?: string; image?: string }>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<"sent" | "failed" | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Every opening starts with an empty window
  useEffect(() => {
    if (!open) return;
    setType("");
    setText("");
    setFile(null);
    setContact(false);
    setErrors({});
    setBusy(false);
    setResult(null);
  }, [open]);

  const pickFile = (picked: File | null) => {
    if (fileInput.current && !picked) fileInput.current.value = "";
    if (!picked) {
      setFile(null);
      setErrors((e) => ({ ...e, image: undefined }));
      return;
    }
    const problem = !IMAGE_TYPES.includes(picked.type as FeedbackImageType) ? t("feedback.errors.imageType") : picked.size > MAX_IMAGE_BYTES ? t("feedback.errors.imageSize") : "";
    if (problem) {
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setErrors((e) => ({ ...e, image: problem }));
      return;
    }
    setFile(picked);
    setErrors((e) => ({ ...e, image: undefined }));
  };

  const submit = async () => {
    const trimmed = text.trim();
    const next: typeof errors = {};
    if (!type) next.type = t("feedback.errors.type");
    if (!trimmed) next.text = t("feedback.errors.textEmpty");
    else if (trimmed.length > MAX_TEXT) next.text = t("feedback.errors.textLong");
    setErrors(next);
    if (next.type || next.text || errors.image) return;
    setBusy(true);
    try {
      const image = file ? { type: file.type as FeedbackImageType, data: await fileToBase64(file) } : null;
      const response = await sendFeedback({ type: type as FeedbackType, text: trimmed, page: pathname, contact, image });
      setResult(response.ok ? "sent" : "failed");
    } catch {
      setResult("failed");
    }
    setBusy(false);
  };

  const over = text.length > MAX_TEXT;

  return (
    <Dialog open={open} onClose={(_, reason) => reason !== "backdropClick" && onClose()} fullWidth maxWidth="sm" aria-labelledby="feedback-title" aria-describedby="feedback-intro">
      <DialogTitle id="feedback-title" sx={{ pr: 7 }}>
        {t("feedback.title")}
        <IconButton onClick={onClose} aria-label={t("feedback.close")} sx={{ position: "absolute", right: 8, top: 8, color: "#ED670A" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography id="feedback-intro" variant="body2" sx={{ mb: 2 }}>
          {t("feedback.intro")}
        </Typography>

        {result === "sent" ? (
          <Alert severity="success" role="status" sx={{ my: 2 }}>
            {t("feedback.success")}
          </Alert>
        ) : (
          <>
            {result === "failed" && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {t("feedback.failure")}
              </Alert>
            )}
            <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2 }}>
              <Typography component="legend" variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t("feedback.typeQuestion")}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1 }}>
                {TYPES.map((item) => (
                  <Box
                    key={item.value}
                    component="label"
                    sx={{
                      cursor: "pointer",
                      border: type === item.value ? `2px solid ${item.color}` : "1px solid #ddd",
                      bgcolor: type === item.value ? `${item.color}14` : "transparent",
                      borderRadius: 2,
                      p: 1.5,
                      minHeight: 44,
                    }}
                  >
                    <input
                      type="radio"
                      name="feedback-type"
                      value={item.value}
                      checked={type === item.value}
                      onChange={() => {
                        setType(item.value);
                        setErrors((e) => ({ ...e, type: undefined }));
                      }}
                      style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: item.color }}>
                      {t(`feedback.types.${item.value}`)}
                    </Typography>
                    {t(`feedback.types.${item.value}Help`) && (
                      <Typography variant="caption" color="text.secondary">
                        {t(`feedback.types.${item.value}Help`)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              {errors.type && (
                <Typography role="alert" variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                  {errors.type}
                </Typography>
              )}
            </Box>

            <TextField
              id="feedback-text"
              label={t("feedback.textLabel")}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (errors.text) setErrors((er) => ({ ...er, text: undefined }));
              }}
              placeholder={t(`feedback.placeholder.${type || "none"}`)}
              multiline
              minRows={5}
              fullWidth
              error={Boolean(errors.text) || over}
              helperText={errors.text || `${t("feedback.noPersonalData")}  ${text.length.toLocaleString("da-DK")} / 4.000`}
              FormHelperTextProps={{ role: errors.text ? "alert" : undefined, sx: { color: over ? "error.main" : undefined } }}
              inputProps={{ "aria-describedby": "feedback-text-helper-text" }}
            />

            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
              {t("feedback.imageLabel")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
              <Button component="label" variant="outlined" size="small" sx={{ color: "#ED670A", borderColor: "#ED670A" }}>
                {t("feedback.imageChoose")}
                <input ref={fileInput} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="feedback-image" />
              </Button>
              {file && (
                <>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                    {file.name}
                  </Typography>
                  <Button size="small" onClick={() => pickFile(null)} sx={{ color: "#ED670A" }}>
                    {t("feedback.imageRemove")}
                  </Button>
                </>
              )}
            </Box>
            <Typography variant="caption" color={errors.image ? "error" : "text.secondary"} role={errors.image ? "alert" : undefined}>
              {errors.image || t("feedback.imageHelp")}
            </Typography>

            <FormControlLabel
              sx={{ mt: 2, alignItems: "flex-start" }}
              control={<Checkbox checked={contact} onChange={(e) => setContact(e.target.checked)} sx={{ color: "#ED670A", "&.Mui-checked": { color: "#ED670A" }, pt: 0 }} />}
              label={<Typography variant="body2">{t("feedback.contact")}</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
              {t("feedback.sentFrom")}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result === "sent" ? (
          <Button onClick={onClose} variant="outlined" sx={{ color: "#ED670A", borderColor: "#ED670A" }}>
            {t("feedback.close")}
          </Button>
        ) : (
          <Button onClick={submit} disabled={busy} variant="contained" sx={{ backgroundColor: "#ED670A", "&:hover": { backgroundColor: "#D55A07" } }}>
            {busy ? t("feedback.sending") : t("feedback.send")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
